/**
 * Runtime URL-pattern shape — the contract between authoring and the two
 * client runtimes (`matchesUrlPattern` in public/tnx-core.js, `tnxMatchUrl` in
 * lambda/bundle-generator/template.js).
 *
 * Why the tolerance suffix: both runtimes compare non-wildcard patterns
 * EXACTLY against `location.pathname`, and stores differ on trailing slashes
 * — TiendaNube serves `/productos/x/` on some themes and `/productos/x` on
 * others. A pattern authored in the wrong form silently never fires (aug-2026:
 * two product-description mods shipped dead this way).
 *
 * Guessing a canonical form just moves the failure to the other kind of store,
 * so we emit a pattern that accepts both. Both runtimes already have a regex
 * branch — tnx-core takes it when the pattern contains `[` or `(`, the bundle
 * takes it as its final fallback — so `/productos/x[/]?` compiles to
 * `^/productos/x[/]?$` in each and matches either form. Verified against both
 * matchers: no sibling-handle collision (`/productos/x-2`) and no subpath leak
 * (`/productos/x/reviews`).
 *
 * Product handles are `[a-z0-9_-]` (PRODUCT_HANDLE_RE), so there are no regex
 * metacharacters to escape.
 */

/** Suffix appended to exact paths so both trailing-slash forms match. */
export const TRAILING_SLASH_TOLERANCE = '[/]?'

/** True when the pattern already carries wildcard / regex syntax of its own. */
function hasMatchSyntax(pattern: string): boolean {
  return pattern.includes('*') || pattern.includes('[') || pattern.includes('(')
}

/**
 * Exact path for a pasted URL or path: pathname only, no query/hash, no
 * trailing slash. This is the display form and the identity used for dedup —
 * never emit it as a runtime pattern on its own.
 */
export function toExactPath(input: string): string {
  const raw = (input || '').trim()
  if (!raw) return ''
  let path: string
  try {
    path = new URL(raw).pathname || '/'
  } catch {
    path = raw.startsWith('/') ? raw : `/${raw}`
  }
  path = path.split(/[?#]/)[0]
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

/**
 * Runtime pattern for a pasted product URL — matches the path with or without
 * its trailing slash. Returns '' when the input yields no path, so callers can
 * skip incomplete rows. Patterns that already carry their own match syntax
 * (and the site root) pass through untouched.
 */
export function toRuntimePattern(input: string): string {
  const raw = (input || '').trim()
  if (hasMatchSyntax(raw)) return raw
  const path = toExactPath(input)
  if (!path || path === '/') return path
  return `${path}${TRAILING_SLASH_TOLERANCE}`
}

/**
 * Human-readable form of a stored pattern — drops the tolerance suffix so
 * consultants see `/productos/x` rather than `/productos/x[/]?`. Display only;
 * never feed the result back to a runtime.
 */
export function displayUrlPattern(pattern: string): string {
  if (!pattern) return pattern
  return pattern.endsWith(TRAILING_SLASH_TOLERANCE)
    ? pattern.slice(0, -TRAILING_SLASH_TOLERANCE.length)
    : pattern
}
